import {z} from 'zod';
import {formatCurrency} from '../../../../Core/helpers/formatCurrency';
import {MAX_RATE_OF_RETURN, MIN_COVERAGE_AMOUNT, MIN_PREMIUM_AMOUNT} from '../../constants';

const nonNegativeNumber = z.union([
    z.number().min(0),
    z.string().refine((val) => val !== '' && !isNaN(Number(val)), {
        message: 'Must be a non-negative number',
    }),
]);

const percentage = z.union([
    z.number().min(0).max(100),
    z.string().refine((val) => val !== '' && !isNaN(Number(val)), {
        message: 'Must be a non-negative number',
    }),
]);
const optionalNumber = z
    .union([
        z.number().min(0),
        z.null(),
        z.string().refine((val) => val === '' || !isNaN(Number(val)), {
            message: 'Must be a number or an empty string',
        }),
    ])
    .optional();

const numberOrM = z
    .union([z.literal('M'), z.number().int().min(1).max(120), z.literal('')])
    .refine(
        (value) => {
            if (value === '') {
                return false;
            }

            const stringValue = typeof value === 'number' ? value.toString() : String(value);
            if (typeof stringValue === 'string' && value === 'M') {
                return true;
            }

            const intValue = parseInt(stringValue, 10);
            return !isNaN(intValue) && intValue >= 1 && intValue <= 120;
        },
        {
            message: 'Please choose a number between 1 - 120 or M',
        },
    )
    .refine(
        (value) => {
            if (typeof value === 'string' && value !== 'M') {
                return false;
            }
            return true;
        },
        {
            message: 'Invalid input. Please choose a number between 1 - 120 or M',
        },
    );

const optionalNumberOrM = z
    .union([z.literal('M'), z.number().int().min(1).max(120), z.literal('')])
    .optional()
    .refine(
        (value) => {
            if (value === undefined || value === '') {
                return true;
            }
            const stringValue = typeof value === 'number' ? value.toString() : String(value);
            if (typeof stringValue === 'string' && value === 'M') {
                return true;
            }
            const intValue = parseInt(stringValue, 10);
            return !isNaN(intValue) && intValue >= 1 && intValue <= 120;
        },
        {
            message: 'Please choose a number between 1 - 120, M, or leave it empty',
        },
    )
    .refine(
        (value) => {
            if (value === undefined || value === '') {
                return true;
            }
            if (typeof value === 'string' && value !== 'M') {
                return false;
            }
            return true;
        },
        {
            message: 'Invalid input. Please choose a number between 1 - 120, M, or leave it empty',
        },
    );

export const createProsperSchema = (age: number, gender: string) => {
    return z
        .object({
            emailAddress: z.string().email('Invalid email address'),
            prosperSmoker: z.boolean().default(false),
            prosperAmplifySolves: z
                .enum([
                    '',
                    'EasySolve',
                    'SureSolve',
                    'OptimalSolve',
                    'OptimizeDeathBenefit',
                    'OptimizeCashAccumulation',
                    'OptimizeIncome',
                ])
                .optional(),
            prosperSpecify: z.enum(['', 'Premium', 'Coverage', '']).optional(),
            prosperAmount: z.union([
                z.number().min(0),
                z.string().refine((val) => val !== '' && !isNaN(Number(val)), {
                    message: 'Must be a non-negative number',
                }),
            ]),
            prosperIllustrationRequest: z.boolean(),
            prosperRiskClass: z.enum([
                'SuperPreferredNonTobacco',
                'PreferredNonTobacco',
                'StandardNonTobacco',
                'PreferredTobacco',
                'StandardTobacco',
                'Super Preferred',
                'Preferred Non Tobacco',
                'Standard Non Tobacco',
                'Preferred Tobacco',
                'Standard Tobacco',
                'Substandard',
            ]),
            prosperTableRating: z.enum(['NotRated', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L']),
            prosperFlatExtra: z.union([
                z.number().min(0).max(10),
                z.string().refine((val) => val !== '' && !isNaN(Number(val)), {
                    message: 'Must be a non-negative number',
                }),
            ]),
            prosperFlatExtraDuration: numberOrM,
            prosperFaceAmountType: z.enum(['Specify', 'Solve', 'Target', 'Guideline', 'SevenPay']),
            prosperFaceAmount: nonNegativeNumber,
            prosperDeathBenefitOption: z.enum(['ALevel', 'BIncreasing']),
            prosperDeathBenefitChangeAge: optionalNumber,
            prosperPremiumType: z.enum(['Specify', 'Solve', 'Target', 'Guideline', 'SevenPay']),
            prosperPremium: nonNegativeNumber,
            prosperPremiumMode: z.enum(['Monthly', 'Quarterly', 'SemiAnnually', 'Annually']),
            prosperLumpSum: nonNegativeNumber,
            prosperPremiumEndYear: numberOrM,
            prosperPremiumAge: optionalNumber,
            prosperIndexName: z.literal('S&P 500'),
            prosperIndexedAllocationPercent: percentage,
            prosperIndexedAccountRateOfReturn: z.union([
                z.number().min(0).max(MAX_RATE_OF_RETURN),
                z.string().refine((val) => val !== '' && !isNaN(Number(val)), {
                    message: 'Must be a non-negative number',
                }),
            ]),
            prosperDistributionSolveType: z.enum(['Specify', 'Solve']),
            prosperDistributionAmount: nonNegativeNumber,
            prosperDistributionType: z.enum(['0', 'IndexedLoan', 'Withdrawal', 'WithdrawaltoBasisthenLoan']),
            prosperDistributionStartAge: optionalNumberOrM,
            prosperDistributionEndAge: optionalNumberOrM,
            prosperAllowMEC: z.boolean(),
            prosperDefinitionOfLifeInsurance: z.enum(['CVAT', 'GPT']),
            prosperSolveGoal: nonNegativeNumber,
            prosperSolveAge: nonNegativeNumber,
        })
        .refine((data) => !(data.prosperSmoker && gender == 'Female' && data.prosperTableRating == 'B'), {
            message: 'Female Substandard Smokers cannot be quoted.',
            path: ['prosperSmoker'],
        })
        .refine(
            (data) => {
                if (data.prosperSpecify === 'Coverage') {
                    if (Number(data.prosperAmount) < MIN_COVERAGE_AMOUNT) {
                        return false;
                    }
                }
                return true;
            },
            {message: `Amount cannot be less than ${formatCurrency(MIN_COVERAGE_AMOUNT)}`, path: ['prosperAmount']},
        )
        .refine(
            (data) => {
                if (data.prosperSpecify === 'Premium') {
                    if (Number(data.prosperAmount) < MIN_PREMIUM_AMOUNT) {
                        return false;
                    }
                }
                return true;
            },
            {message: `Amount cannot be less than ${formatCurrency(MIN_PREMIUM_AMOUNT)}`, path: ['prosperAmount']},
        )
        .refine(
            (data) => {
                const nonTobaccoClasses = ['SuperPreferredNonTobacco', 'PreferredNonTobacco', 'StandardNonTobacco'];
                const tobaccoClasses = ['PreferredTobacco', 'StandardTobacco'];

                if (data.prosperSmoker === false) {
                    return nonTobaccoClasses.includes(data.prosperRiskClass);
                } else if (data.prosperSmoker === true) {
                    return tobaccoClasses.includes(data.prosperRiskClass);
                }
                return true;
            },
            {
                message: `Risk Class must align with tobacco usage.`,
                path: ['prosperRiskClass'],
            },
        )
        .refine(
            (data) => {
                if (data.prosperDistributionStartAge && data.prosperDistributionEndAge) {
                    if (data.prosperDistributionStartAge > data.prosperDistributionEndAge) {
                        return false;
                    }
                }
                return true;
            },
            {message: 'Start age must come before end age.', path: ['prosperDistributionEndAge']},
        )
        .refine(
            (data) => {
                const isFaceAmountSpecify = data.prosperFaceAmountType === 'Specify';
                const isPremiumSpecify = data.prosperPremiumType === 'Specify';

                return (isFaceAmountSpecify && isPremiumSpecify) || isFaceAmountSpecify || isPremiumSpecify;
            },
            {
                message: "Face Amount Type or Premium Type be set to 'Specify'",
                path: ['prosperFaceAmountType'], // This will make the error appear on both fields
            },
        )
        .refine(
            (data) => {
                const isFaceAmountSpecify = data.prosperFaceAmountType === 'Specify';
                const isPremiumSpecify = data.prosperPremiumType === 'Specify';

                return (isFaceAmountSpecify && isPremiumSpecify) || isFaceAmountSpecify || isPremiumSpecify;
            },
            {
                message: "Face Amount Typer or Premium Type be set to 'Specify'",
                path: ['prosperPremiumType'],
            },
        )
        .refine(
            (data) => {
                if (data.prosperSpecify === 'Coverage') {
                    return Number(data.prosperPremium) <= Number(data.prosperFaceAmount);
                }
                return true;
            },
            {
                message: 'Face Amount must be greater than Premium',
                path: ['prosperPremium'],
            },
        )
        .refine(
            (data) => {
                if (data.prosperSpecify === 'Coverage') {
                    return Number(data.prosperPremium) <= Number(data.prosperFaceAmount);
                }
                return true;
            },
            {
                message: 'Face Amount must be greater than Premium',
                path: ['prosperFaceAmount'],
            },
        )
        .refine(
            (data) => {
                if (data.prosperDeathBenefitOption !== 'ALevel') {
                    if (Number(data.prosperDeathBenefitChangeAge) < age) {
                        return false;
                    }
                }
                return true;
            },
            {
                message: 'Death Benefit Change Age cannot be less than issue age',
                path: ['prosperDeathBenefitChangeAge'],
            },
        )
        .refine(
            (data) => {
                if (data.prosperDeathBenefitOption !== 'ALevel') {
                    if (Number(data.prosperDistributionStartAge) < age) {
                        return false;
                    }
                }
                return true;
            },
            {
                message: 'Start Age cannot be less than issue age',
                path: ['prosperDistributionStartAge'],
            },
        );
};
export type ProsperFormValues = z.infer<ReturnType<typeof createProsperSchema>>;
